import { useLayoutEffect, useRef, useState, RefObject } from 'react';
import { RecoilState, useRecoilCallback } from 'recoil';
import { isEqual, pick, uniqueId } from 'lodash';
import useResizeObserver, {
  ObservedSize,
} from 'packages/lib/utils/hooks/use-resize-observer';

export const useSyncElementSize = (
  elementSizeAtomFam: (
    param: string
  ) => RecoilState<DOMRectReadOnly | null | undefined>,
  el?: HTMLDivElement | null,
  shouldRecursivelyAwaitElement?: boolean
): null => {
  // NOTE: optimization to prevent re-renders due to object reconstruction
  const bboxProps = [
    'width',
    'height',
    'x',
    'y',
    'left',
    'right',
    'top',
    'bottom',
  ];
  const [tick, setTick] = useState(uniqueId());
  const syncHeight = useRecoilCallback(
    ({ set }) =>
      (entries: ResizeObserverEntry[]) => {
        const [entry] = entries;
        set(elementSizeAtomFam(entry?.target.id || ''), (prev) => {
          const next = entry.contentRect;
          // NOTE: optimization to prevent re-renders due to object reconstruction
          if (isEqual(pick(prev, bboxProps), pick(next, bboxProps))) {
            return prev;
          }
          return next;
        });
      }
  );
  const observerRef = useRef(new ResizeObserver(syncHeight));
  useLayoutEffect((): (() => void) => {
    if (!el && shouldRecursivelyAwaitElement) {
      setTimeout(() => {
        setTick(uniqueId());
      }, 100);
    }
    const { current: observer } = observerRef;
    if (el) {
      observer.observe(el);
    }
    return () => el && observer.unobserve(el);
  }, [syncHeight, tick, shouldRecursivelyAwaitElement, el]);

  return null;
};

/**
 * Utility function to get an element's size without using recoil. This is
 * useful when NOT needing the size in a global context, but rather locally.
 * https://www.npmjs.com/package/@react-hook/resize-observer
 * @param target Element reference created by using createRef() or useRef()
 * @returns ObservedSize object of { width: number; height: number; }
 */
export const useElementSize = (
  target: RefObject<HTMLElement | SVGSVGElement>
): ObservedSize | null => {
  const [size, setSize] = useState<ObservedSize | null>(null);

  useLayoutEffect(() => {
    if (!target?.current) return;
    const { width, height } = target.current.getBoundingClientRect();
    setSize({ width, height });
  }, [target]);

  useResizeObserver({
    ref: target,
    onResize: (observedSize) => setSize(observedSize),
  });
  return size;
};
